import { useAtomValue } from "@effect/atom-react";
import * as Cause from "effect/Cause";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect } from "react";

import { appAtomRegistry } from "../../rpc/atomRegistry";
import { fetchSessionState } from "./auth";

const primarySessionStateAtom = Atom.make(Effect.promise(fetchSessionState)).pipe(
  Atom.swr({ staleTime: 5_000, revalidateOnMount: true }),
  Atom.setIdleTTL(5 * 60_000),
  Atom.withLabel("primary-environment:session"),
);

export function refreshPrimarySessionState(): void {
  appAtomRegistry.refresh(primarySessionStateAtom);
}

const SESSION_STATE_POLL_INTERVAL_MS = 60_000;

/**
 * Re-polls `GET /api/auth/session` every ~60s so a revocation on the server
 * surfaces in the UI within about a minute, with the 15-minute
 * token expiry as the outer backstop. Never polls faster than this — the
 * server's own 30s connection sweep is what actually closes a revoked
 * connection; this poll only moves how quickly the UI reflects it.
 *
 * Skips the tick while the tab is hidden: a background tab polling forever
 * is pure battery/request cost for no benefit, since the next foreground
 * tick catches up immediately.
 */
export function usePrimarySessionStatePolling(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const interval = setInterval(() => {
      if (document.visibilityState === "hidden") {
        return;
      }
      refreshPrimarySessionState();
    }, SESSION_STATE_POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
    };
  }, [enabled]);
}

export function usePrimarySessionState() {
  const result = useAtomValue(primarySessionStateAtom);
  const refresh = useCallback(() => {
    refreshPrimarySessionState();
  }, []);
  let error: string | null = null;
  if (result._tag === "Failure") {
    const cause = Cause.squash(result.cause);
    error = cause instanceof Error ? cause.message : "Could not read environment session.";
  }
  return {
    data: Option.getOrNull(AsyncResult.value(result)),
    error,
    isPending: result.waiting,
    refresh,
  };
}
